import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  InputField,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "../index.scss";
import { getUserInfoFromLocalStorage } from "../../../shared/utils/auth";
import { LoginDto } from "../../../types/auth";
import utilsCommon from "../../../shared/utils/common";
import { nanoid } from "@reduxjs/toolkit";
import useWorkgroupStore from "../../../redux/dispatcher/useWorkgroupStore";
import useLoginStore from "../../../redux/dispatcher/useLoginStore";
import { t } from "i18next";
interface ChangePWTabProps {
  open: boolean;
}

const ChangePWTab: React.FC<ChangePWTabProps> = ({ open }) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 정리 */
  const toasts = useToast();
  const { changeMemberInfo } = useWorkgroupStore();
  const { sessionLogout } = useLoginStore();

  /** State 정리 */
  const [password, changePassword] = useState({
    original: "",
    check: "",
  }); // 사용자가 적은 비밀번호 및 확인
  const [isPwVisible, togglePWVisible] = useState({
    original: false,
    check: false,
  }); // 비밀번호 보임, 안보임 처리용
  const [isPWChangable, togglePWChangable] = useState(false); // 비밀번호 수정 화면 view, edit
  const [err, changeErr] = useState("NONE"); // 에러가 있을경우 사용

  /** useEffect 정리 */
  useEffect(() => {
    if (open) {
      changePassword({ original: "", check: "" });
      togglePWVisible({
        original: false,
        check: false,
      });
      togglePWChangable(false);
      changeErr("NONE");
    }
  }, [open]);
  useEffect(() => {
    changePassword({ original: "", check: "" });
    togglePWVisible({
      original: false,
      check: false,
    });
  }, [isPWChangable]);

  /** 함수 정리 */
  /** 비밀번호 변경 function */
  const handleUpdatePassword = async () => {
    /** 에러 1: 비밀번호가 유효하지 않음. */
    if (!utilsCommon.passwordValidation(password.original)) {
      changeErr("INVALID_PW_TYPE");
      return;
    }
    /** 에러 2: 새로운 비밀번호와 새로운 비밀번호 확인 값이 상이함 */
    if (password.original !== password.check) {
      changeErr("NOT_MATCH_WITH_ORIGINAL");
      return;
    }
    let resp: any = await changeMemberInfo([
      { password: password.check, userId: loginDto.userInfo.userId },
    ]);
    if (resp.payload) {
      sessionLogout(); // 비밀번호 성공시 로그아웃
      window.location.href = `${process.env.PUBLIC_URL}/login?toastMsg=login.changePW.alert.success`;
    } else if (resp.error) {
      toasts.toastMsg(nanoid(), resp.error.message, "error");
      changeErr("");
    }
  };
  /** UI: 비밀번호 에러 리소스 */
  const renderPWErrorStr = (type: string) => {
    switch (type) {
      case "INVALID_PW_TYPE":
        return t("header.security.alert.invalidPW");
      case "NOT_MATCH_WITH_ORIGINAL":
        return t("login.changePW.alert.notMatchPwMsg");
    }
    return undefined;
  };

  return isPWChangable ? (
    <Box classes="profileModal-body">
      <Typography type="subtitle2">{t("header.security.newPW")}</Typography>
      <InputField
        placeholder={t("header.security.newPWPlaceholder")}
        maxLength={60}
        size={"md"}
        classes="mg mt-6"
        value={password.original}
        onChange={(e) =>
          changePassword({ ...password, original: e.target.value })
        }
        type={isPwVisible.original ? "text" : "password"}
        trailingIcon={
          <Box alignItems={"center"}>
            <Tooltip
              message={
                isPwVisible.original
                  ? t("login.login.hidePW")
                  : t("login.login.showPW")
              }
            >
              <span
                className={"pwViewBtn"}
                onClick={() =>
                  togglePWVisible({
                    ...isPwVisible,
                    original: !isPwVisible.original,
                  })
                }
              >
                <Icons
                  variant="line"
                  label="preview"
                  isOn={!isPwVisible.original}
                  stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
                />
              </span>
            </Tooltip>
          </Box>
        }
        isError={err === "INVALID_PW_TYPE"}
        helpMessage={
          err === "INVALID_PW_TYPE"
            ? renderPWErrorStr(err)
            : t("header.security.pwInfo")
        }
      />
      <Typography type="subtitle2" classes="mg mt-32">
        {t("header.security.confirmNewPW")}
      </Typography>
      <InputField
        maxLength={60}
        size={"md"}
        classes="mg mt-6"
        placeholder={t("header.security.confirmPWPlaceholder")}
        value={password.check}
        onChange={(e) => changePassword({ ...password, check: e.target.value })}
        type={isPwVisible.check ? "text" : "password"}
        trailingIcon={
          <Box alignItems={"center"}>
            <Tooltip
              message={
                isPwVisible.check
                  ? t("login.login.hidePW")
                  : t("login.login.showPW")
              }
            >
              <span
                className={"pwViewBtn"}
                onClick={() =>
                  togglePWVisible({
                    ...isPwVisible,
                    check: !isPwVisible.check,
                  })
                }
              >
                <Icons
                  variant="line"
                  label="preview"
                  isOn={!isPwVisible.check}
                  stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
                />
              </span>
            </Tooltip>
          </Box>
        }
        isError={err.includes("NOT_MATCH_WITH_ORIGINAL")}
        helpMessage={
          err === "NOT_MATCH_WITH_ORIGINAL" ? renderPWErrorStr(err) : ""
        }
      />
      <Box height={88} classes="mg mt-auto ml-auto" alignItems="flex-end">
        <Button
          btnStyle="fill"
          label={t("common.cancel")}
          type="secondary"
          onClick={() => togglePWChangable(false)}
        />
        <Button
          btnStyle="fill"
          label={t("common.save")}
          classes="mg ml-8"
          onClick={handleUpdatePassword}
          state={
            password.original === "" || password.check === ""
              ? "disable"
              : "default"
          }
        />
      </Box>
    </Box>
  ) : (
    <Box classes="profileModal-body">
      <Typography type="subtitle2">{t("login.login.password")}</Typography>
      <span className="profileModal-divider" style={{ marginBottom: "16px" }} />
      <Typography type="body2_rg" exactColor={getColor("COLOR_TEXT_SECONDARY")}>
        {t("header.security.passwordDesc")}
      </Typography>
      <Button
        label={t("header.security.changePWBtn")}
        btnStyle="fill"
        size="lg"
        width="fit-content"
        leadingIcon={<Icons variant="line" label="edit" />}
        classes="mg mt-16"
        onClick={() => togglePWChangable(true)}
      />
    </Box>
  );
};

export default ChangePWTab;
